import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { IoMdPrint } from "react-icons/io";
import { IoArrowBack } from "react-icons/io5";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import logo from "../../icons/logo.png";
import paid1 from "../../icons/paid1.png";
import { encodedImagesURI } from "../../utils/helpers";
import BankVoucherLayout from "../../base-components/BankVoucherLayout";
import MemberPortalGuard from "./MemberPortalGuard";
import { getMemberInvoiceDetail, initiateMemberInvoicePayment } from "../../api/memberPortalClient";
import { submitPaymentForm } from "../../utils/submitPaymentForm";
import Popup from "../../base-components/Popup";
import { formatMoney } from "./components/MemberUI";

/**
 * Print/screen styles are scoped to `.mi-*` classes and live inside the default
 * (non-bank) layout only, so companies using BankVoucherLayout's landscape
 * 3-copy voucher keep their own page setup.
 */
const A4_STYLES = `
.mi-sheet {
  width: 210mm;
  min-height: 297mm;
  padding: 14mm 15mm;
  margin: 0 auto;
  background: #fff;
  color: #111827;
  font-size: 10pt;
  line-height: 1.45;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.mi-screen-frame {
  background: #eef2f6;
  padding: 16px 8px 32px;
  overflow-x: auto;
}
.mi-screen-frame .mi-sheet {
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
  border-radius: 2px;
}
.mi-title { font-size: 22pt; font-weight: 700; letter-spacing: 0.06em; }
.mi-label { font-size: 8pt; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7280; font-weight: 700; }
.mi-h { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.06em; color: #374151; font-weight: 700; }
.mi-table { width: 100%; border-collapse: collapse; }
.mi-table th, .mi-table td { padding: 6pt 4pt; }
.mi-table thead th {
  border-bottom: 1.5pt solid #111827;
  text-align: left;
  font-size: 8.5pt;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.mi-table tbody td { border-bottom: 0.5pt solid #e5e7eb; }
.mi-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.mi-totals { width: 72mm; border-collapse: collapse; }
.mi-totals td { padding: 5pt 2pt; border-bottom: 0.5pt solid #e5e7eb; }
.mi-totals tr.mi-grand td {
  border-top: 1.2pt solid #111827;
  border-bottom: 1.2pt solid #111827;
  font-weight: 700;
  font-size: 11pt;
}
.mi-badge {
  display: inline-block;
  padding: 3pt 8pt;
  border-radius: 999px;
  font-size: 8.5pt;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.mi-badge-paid { background: #ecfdf5; color: #047857; border: 0.5pt solid #a7f3d0; }
.mi-badge-due { background: #fef2f2; color: #b91c1c; border: 0.5pt solid #fecaca; }

@media print {
  @page { size: A4 portrait; margin: 0; }
  .mi-screen-frame { background: #fff !important; padding: 0 !important; overflow: visible !important; }
  .mi-screen-frame .mi-sheet { box-shadow: none !important; border-radius: 0 !important; }
  .mi-sheet {
    width: 210mm;
    min-height: 297mm;
    margin: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .mi-no-print { display: none !important; }
}
`;

function getPrintTimestamp() {
  return dayjs().format("MMM DD, YYYY hh:mm A");
}

function fmtDate(value) {
  return value ? dayjs(value).format("MMM DD, YYYY") : "—";
}

function MemberInvoiceViewContent() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: printRef });

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { ok, body } = await getMemberInvoiceDetail(invoiceId);
        if (cancelled) return;
        if (!ok) {
          setError(body?.error?.message || "Failed to load invoice.");
          setPopupOpen(true);
          return;
        }
        setDetail(body.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load invoice.");
          setPopupOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  const handlePayNow = async () => {
    if (paying) return;
    setPaying(true);
    setError("");
    try {
      const { ok, body } = await initiateMemberInvoicePayment(invoiceId);
      if (!ok) {
        setError(body?.error?.message || "Could not start online payment.");
        setPopupOpen(true);
        return;
      }

      const payment = body.data || {};
      submitPaymentForm(
        payment.payment_portal_url,
        payment.form_fields,
        payment.submit_method || "POST"
      );
    } catch (err) {
      setError(err.message || "Could not start online payment.");
      setPopupOpen(true);
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <CircularProgress sx={{ color: "#D71920" }} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-6">
        <button
          type="button"
          onClick={() => navigate("/member/invoices")}
          className="flex items-center gap-2 text-gx-ink hover:text-gx-red"
        >
          <IoArrowBack className="text-2xl" /> Back to invoices
        </button>
        <p className="mt-6 text-gx-ink-soft">Invoice not found.</p>
        {popupOpen && (
          <Popup
            alertType="error"
            message={error}
            onClose={() => setPopupOpen(false)}
          />
        )}
      </div>
    );
  }

  const company = detail.company || {};
  const location = detail.location || {};
  const member = detail.member || {};
  const pkg = detail.package || {};
  const voucher = detail.voucher || {};
  const invoice = detail.invoice || {};
  const subscription = detail.subscription || {};
  const transaction = detail.transaction || {};
  const companyId = company._id;
  const facilities = pkg.facilities || [];
  const otherCharges = invoice.other_charges;
  const isPaid = invoice.is_paid === true;
  const canPayOnline =
    invoice.can_pay === true && company.can_pay_online === true;
  const payableAmount =
    invoice.payable_amount != null ? invoice.payable_amount : invoice.pending_amount;

  const lineItems = [];
  if (invoice.membership_fee > 0) {
    lineItems.push({ label: "Membership Fee", amount: invoice.membership_fee });
  }
  if (invoice.registration_fee > 0) {
    lineItems.push({
      label: "Registration Fee",
      amount: invoice.registration_fee,
    });
  }
  if (invoice.trainer_fee > 0) {
    lineItems.push({
      label: `Trainer Fee${
        voucher.trainer_id?.name ? ` (${voucher.trainer_id.name})` : ""
      }`,
      amount: invoice.trainer_fee,
    });
  }
  if (invoice.trainer_fee === 0) {
    for (const facility of facilities) {
      lineItems.push({
        label: facility.name,
        amount: facility.fee,
        complimentary: facility.fee === 0,
      });
    }
  }
  if (otherCharges?.name) {
    lineItems.push({ label: otherCharges.name, amount: otherCharges.amount });
  }

  return (
    <div>
      {/* Toolbar — Back + Pay (when eligible) + Print */}
      <div className="mi-no-print flex justify-between items-center px-4 md:px-8 py-3 bg-white border-b border-gx-border">
        <button
          type="button"
          onClick={() => navigate("/member/invoices")}
          className="flex items-center gap-2 text-sm font-medium text-gx-ink hover:text-gx-red"
        >
          <IoArrowBack className="text-2xl" />
          <span className="hidden sm:inline">Back to invoices</span>
        </button>
        <div className="flex items-center gap-2">
          {canPayOnline ? (
            <button
              type="button"
              onClick={handlePayNow}
              disabled={paying}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#059669] text-white text-sm font-semibold hover:bg-[#047857] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {paying ? (
                <>
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                  Redirecting…
                </>
              ) : (
                <>Pay {formatMoney(payableAmount)}</>
              )}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => reactToPrintFn()}
            disabled={paying}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-gx-red text-white text-sm font-semibold hover:bg-[#b5151b] transition disabled:opacity-60"
          >
            <IoMdPrint className="text-xl" />
            Print / Save PDF
          </button>
        </div>
      </div>

      <div ref={printRef}>
        <BankVoucherLayout
          company_id={companyId}
          voucherData={{
            invoice_no: voucher.invoice_no,
            voucher_no: voucher.invoice_no,
            voucher_type: voucher.voucher_type,
            voucher_date: voucher.voucher_date,
            due_date: invoice.due_date,
            registration_fee: invoice.registration_fee,
            membership_fee: invoice.membership_fee,
            trainer_fee: invoice.trainer_fee,
            facilities_fee: invoice.facilities_fee,
            other_charges_amount: otherCharges?.amount,
            other_charges_name: otherCharges?.name,
            discount: invoice.discount,
            package_name: pkg.title,
            is_paid: invoice.is_paid,
            payment_status: invoice.payment_status,
            payment_date: invoice.payment_date,
            reference_number: voucher.reference_number,
            payment_method: voucher.payment_method,
            invoice,
            total_amount: invoice.total_amount,
            pending_amount: invoice.pending_amount,
          }}
          companyData={company}
          memberData={{
            member_id: member.member_id,
            name: member.name,
            phone: member.phone,
            package_name: pkg.title,
          }}
        >
          <style>{A4_STYLES}</style>

          <div className="mi-screen-frame">
            <div className="mi-sheet">
              {/* Letterhead */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  borderBottom: "1.5pt solid #111827",
                  paddingBottom: "8mm",
                }}
              >
                <div style={{ display: "flex", gap: "6mm", alignItems: "flex-start" }}>
                  <img
                    src={
                      company.company_logo
                        ? encodedImagesURI(company.company_logo)
                        : logo
                    }
                    alt=""
                    style={{
                      width: "28mm",
                      maxHeight: "24mm",
                      objectFit: "contain",
                    }}
                  />
                  <div>
                    <div style={{ fontSize: "13pt", fontWeight: 700 }}>
                      {company.company_name || "—"}
                    </div>
                    <div style={{ color: "#4b5563", marginTop: "1mm" }}>
                      {location.name ? <div>{location.name}</div> : null}
                      {location.address ? <div>{location.address}</div> : null}
                      {location.phone_number ? (
                        <div>Tel: {location.phone_number}</div>
                      ) : null}
                      {location.email ? <div>{location.email}</div> : null}
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <div className="mi-title">INVOICE</div>
                  <div style={{ marginTop: "2mm" }}>
                    <span className="mi-label">No.&nbsp;</span>
                    <span style={{ fontWeight: 700 }}>
                      {voucher.invoice_no || "—"}
                    </span>
                  </div>
                  <div style={{ marginTop: "3mm" }}>
                    <span
                      className={`mi-badge ${
                        isPaid ? "mi-badge-paid" : "mi-badge-due"
                      }`}
                    >
                      {invoice.payment_status || (isPaid ? "Paid" : "Pending")}
                    </span>
                  </div>
                  {isPaid ? (
                    <img
                      src={paid1}
                      alt="Paid"
                      style={{
                        width: "30mm",
                        objectFit: "contain",
                        marginTop: "3mm",
                        marginLeft: "auto",
                      }}
                    />
                  ) : null}
                </div>
              </div>

              {/* Billed to / meta */}
              <div
                style={{
                  display: "flex",
                  gap: "10mm",
                  marginTop: "8mm",
                }}
              >
                <div style={{ width: "50%" }}>
                  <div className="mi-h">Billed to</div>
                  <div style={{ marginTop: "2mm" }}>
                    <div style={{ fontSize: "11pt", fontWeight: 700 }}>
                      {member.name || "—"}
                    </div>
                    <div style={{ color: "#4b5563", marginTop: "1mm" }}>
                      {member.address ? <div>{member.address}</div> : null}
                      {member.phone ? <div>{member.phone}</div> : null}
                      {member.email ? <div>{member.email}</div> : null}
                      <div>
                        Member ID: {member.member_code || member.member_id || "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ width: "50%" }}>
                  <div className="mi-h">Invoice details</div>
                  <table
                    className="mi-table"
                    style={{ marginTop: "2mm", fontSize: "9.5pt" }}
                  >
                    <tbody>
                      <tr>
                        <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                          Invoice date
                        </td>
                        <td className="mi-num" style={{ fontWeight: 600 }}>
                          {fmtDate(voucher.voucher_date)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                          Due date
                        </td>
                        <td className="mi-num" style={{ fontWeight: 600 }}>
                          {fmtDate(invoice.due_date)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                          Package
                        </td>
                        <td className="mi-num" style={{ fontWeight: 600 }}>
                          {pkg.title || "—"}
                          {pkg.duration ? ` (${pkg.duration})` : ""}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                          Subscription period
                        </td>
                        <td className="mi-num" style={{ fontWeight: 600 }}>
                          {invoice.billing_period_start ||
                          invoice.billing_period_end ||
                          subscription.renewed_date ||
                          subscription.end_date
                            ? `${fmtDate(
                                invoice.billing_period_start || subscription.renewed_date
                              )} – ${fmtDate(
                                invoice.billing_period_end || subscription.end_date
                              )}`
                            : "—"}
                        </td>
                      </tr>
                      {company.has_bank_integration === true ? (
                        <tr>
                          <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                            Bank voucher no.
                          </td>
                          <td className="mi-num" style={{ fontWeight: 600 }}>
                            {voucher.transaction_number || "N/A"}
                          </td>
                        </tr>
                      ) : null}
                      {invoice.payment_date ? (
                        <tr>
                          <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                            Paid on
                          </td>
                          <td className="mi-num" style={{ fontWeight: 600 }}>
                            {fmtDate(invoice.payment_date)}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Charges */}
              <table className="mi-table" style={{ marginTop: "9mm" }}>
                <thead>
                  <tr>
                    <th style={{ paddingLeft: 0 }}>Description</th>
                    <th className="mi-num" style={{ width: "40mm" }}>
                      Amount (PKR)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr>
                      <td style={{ paddingLeft: 0, color: "#6b7280" }}>
                        {invoice.invoice_type || "Invoice"}
                      </td>
                      <td className="mi-num">
                        {formatMoney(invoice.total_amount)}
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((item, idx) => (
                      <tr key={`${item.label}-${idx}`}>
                        <td style={{ paddingLeft: 0 }}>{item.label}</td>
                        <td className="mi-num">
                          {item.complimentary
                            ? "Complimentary"
                            : formatMoney(item.amount)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Payment info + totals */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10mm",
                  marginTop: "8mm",
                }}
              >
                <div style={{ width: "50%" }}>
                  <div className="mi-h">Payment information</div>
                  <table
                    className="mi-table"
                    style={{ marginTop: "2mm", fontSize: "9.5pt" }}
                  >
                    <tbody>
                      <tr>
                        <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                          Method
                        </td>
                        <td style={{ fontWeight: 600 }}>
                          {voucher.payment_method || (isPaid ? "Bank" : "N/A")}
                        </td>
                      </tr>
                      {voucher.payment_method &&
                      voucher.payment_method !== "Cash" ? (
                        <tr>
                          <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                            Reference
                          </td>
                          <td style={{ fontWeight: 600 }}>
                            {voucher.reference_number || "N/A"}
                          </td>
                        </tr>
                      ) : null}
                      {invoice.discount_remarks ? (
                        <tr>
                          <td style={{ color: "#6b7280", paddingLeft: 0 }}>
                            Discount remarks
                          </td>
                          <td>{invoice.discount_remarks}</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>

                <div>
                  <table className="mi-totals">
                    <tbody>
                      <tr>
                        <td style={{ color: "#6b7280" }}>Subtotal</td>
                        <td className="mi-num">
                          {formatMoney(invoice.total_amount)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ color: "#6b7280" }}>Discount</td>
                        <td className="mi-num">
                          {formatMoney(invoice.discount)}
                        </td>
                      </tr>
                      <tr className="mi-grand">
                        <td>Total</td>
                        <td className="mi-num">
                          {formatMoney(invoice.pending_amount)}
                        </td>
                      </tr>
                      {invoice.payment_status === "Paid" ? (
                        <tr>
                          <td style={{ color: "#6b7280" }}>Paid</td>
                          <td className="mi-num">
                            {formatMoney(voucher.paid_amount || 0)}
                          </td>
                        </tr>
                      ) : (
                        <>
                          <tr>
                            <td style={{ color: "#6b7280" }}>
                              Previous balance
                            </td>
                            <td className="mi-num">
                              {formatMoney(transaction.balance_before || 0)}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ color: "#6b7280" }}>Pending</td>
                            <td className="mi-num">
                              {formatMoney(invoice.pending_amount)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer pinned to the bottom of the A4 page */}
              <div
                style={{
                  marginTop: "auto",
                  paddingTop: "10mm",
                  borderTop: "0.5pt solid #e5e7eb",
                  color: "#6b7280",
                  fontSize: "8.5pt",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "6mm",
                }}
              >
                <span>
                  This is a computer generated invoice. For payments and
                  queries, please contact your club.
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  Printed on {getPrintTimestamp()}
                </span>
              </div>
            </div>
          </div>
        </BankVoucherLayout>
      </div>

      {popupOpen && (
        <Popup
          alertType="error"
          message={error}
          onClose={() => setPopupOpen(false)}
        />
      )}
    </div>
  );
}

function MemberInvoiceView() {
  return (
    <MemberPortalGuard>
      <MemberInvoiceViewContent />
    </MemberPortalGuard>
  );
}

export default MemberInvoiceView;
